import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { LoaderService } from '../../../core/services/loader.service';
import { CommonService } from '../../../shared/services/common.service';
import { ManageSettingsService } from '../manage-settings.service';
import { debounceTime, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { lmsSettingModel } from '../manage-settings.interface';
import { ErrorResponse } from '../../../core/models/error.models';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AllowOnlyValidNumbersDirective } from '../../../core/direactives/allowedOnlyValidNUmber.directive';
import { OrganizationDetailModel } from '../../organizations/organization.interface';
import { AuthService } from '../../../core/services/auth.service';
import { SettingMode } from '../manage-settings.enums';
import { ActivatedRoute } from '@angular/router';
import { OrganizationSelectionService } from '../../../core/services/organization-selection.service';
import { ManageOrganizationDrawerParams } from '../../manage-organization/manage-organization.interface';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../core/services/drawer.service';
import { OrgManagementMode } from '../../manage-organization/manage-organization.enums';

@Component({
  selector: 'app-lms-settings',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputModule,
    NzCheckboxModule,
    ReactiveFormsModule,
    CommonModule,
    NzButtonModule,
    NzSpinModule,
    NgxMaskDirective,
    AllowOnlyValidNumbersDirective,
  ],
  providers: [provideNgxMask()],
  templateUrl: './lms-settings.component.html',
  styleUrl: './lms-settings.component.scss',
})
export class LmsSettingsComponent implements OnDestroy {
  lmsForm!: FormGroup;
  organizationId = 0;
  lmsSettings: lmsSettingModel | null = null;
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  orgData: OrganizationDetailModel | null = null;
  mode = SettingMode.EDIT;
  @Input() isMangeOption = false;
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
  };

  constructor(
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private commonService: CommonService,
    private cd: ChangeDetectorRef,
    private manageSettingService: ManageSettingsService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private orgSelectionService: OrganizationSelectionService,
    private readonly drawerService: DrawerService
  ) {
    this.setupDrawerParams();

    this.loading$ = this.loaderService.loading$;

    this.initForm();
    this.orgSelectionService.orgId$
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(res => {
        if (res) {
          this.organizationId = res;
          this.updateFormOrgId();
          this.getLmsSettings();
        } else {
          this.getOrgIdFromUrl();
        }
      });
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  private initForm(): void {
    this.lmsForm = this.fb.group({
      sendLmsDeadlineEmails: [false],
      defaultLmsDeadlineDays: [{ value: null }, Validators.required],
      organizationId: [this.organizationId],
      id: [0],
    });
  }

  private getOrgIdFromUrl(): void {
    if (this.orgParams.organizationId) {
      this.organizationId = this.orgParams.organizationId;
      this.orgData = {
        organizationId: this.organizationId,
      } as OrganizationDetailModel;
      this.updateFormOrgId();
      this.getLmsSettings();
    }
  }

  private updateFormOrgId(): void {
    this.lmsForm.patchValue({
      organizationId: this.organizationId,
    });
  }

  getLmsSettings() {
    if (this.organizationId) {
      this.loaderService.show();
      this.manageSettingService
        .getLmsSettingsByOrg(this.organizationId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            this.loaderService.hide();
            if (response.result) {
              this.lmsSettings = response.result;
              this.mode = SettingMode.VIEW;
              this.lmsForm.patchValue({
                ...this.lmsSettings,
                organizationId: this.organizationId,
              });

              this.cd.detectChanges();
            } else {
              this.lmsForm.reset({
                sendLmsDeadlineEmails: false,
                defaultLmsDeadlineDays: null,
                organizationId: this.organizationId,
                id: 0,
              });
              this.mode = SettingMode.VIEW;
              this.cd.detectChanges();
            }
          },
          error: error => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    }
  }

  submitForm() {
    this.loaderService.show();

    this.lmsForm.patchValue({
      organizationId: this.organizationId,
    });

    const operation$ = this.manageSettingService.saveLmsSettings(
      this.lmsForm.value
    );

    operation$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.cd.detectChanges();
        this.commonService.notify('success', `LMS settings saved successfully`);
        this.getLmsSettings();
      },
      error: error => {
        this.loaderService.hide();
        this.handleError(error);
      },
    });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }
  onCancel() {
    this.getLmsSettings();
  }

  validateInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value.trim();
    if (value === '') {
      this.lmsForm.patchValue({
        defaultLmsDeadlineDays: '',
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
